/**
 * @file stationActivityApi.ts
 *
 * Data layer for the Station Admin dashboard. Everything here is scoped to the
 * caller's OWN station: the RMS and evidence backends derive the station from
 * the WorkOS authorizer's org_id, so no stationId is ever sent from the client
 * (a station_admin cannot query another org, and — unlike GET /analytics, which
 * is hard-gated to org-karda-admin — GET /reports + GET /evidence honor the
 * station_admin role's reports:read_station / evidence:read_station grants).
 */
import { rmsClient } from "@/shared/api/clients";
import aiClient from "@/shared/api/clients/aiClient";
import axios from "axios";
import type { ReportGenReport } from "@/shared/interfaces/ReportGenReport";
import { ReportStatus } from "@/shared/interfaces/ReportStatus";
import type { EvidenceFileDDB } from "@/shared/interfaces/EvidenceFileDDB";
import {
  fetchEvidencePage,
  type EvidenceListFilters,
} from "@/features/evidence/api/evidenceQueries";

/** Cap on how much we page through for a single window render. Stations with
 *  more activity than this in one login gap are rare; when hit we flag the
 *  metric as a lower bound rather than silently under-reporting. */
export const REPORTS_FETCH_LIMIT = 1000;
export const EVIDENCE_PAGE_CAP = 10;
/** Server max page size (default is 50) — halves the sequential round trips
 *  and lifts the truncation ceiling to CAP × SIZE = 1,000, matching reports. */
export const EVIDENCE_PAGE_SIZE = 100;

/** Report statuses that represent a signed/finalized report. Exported so UI
 *  surfaces (e.g. the feed's status chips) share one status taxonomy. */
export const SIGNED_STATUSES: ReadonlySet<string> = new Set([
  ReportStatus.SIGNED,
  ReportStatus.CO_APPROVED,
  ReportStatus.RMS_COMMITTED,
]);

/** Report statuses that represent a report awaiting human review. */
export const IN_REVIEW_STATUSES: ReadonlySet<string> = new Set([
  ReportStatus.PENDING_REVIEW,
  ReportStatus.IN_CO_REVIEW,
  ReportStatus.CHANGES_REQUESTED,
]);

export interface StationActivityWindow {
  /** ISO start of the window (admin's previous login, or 48h fallback). */
  start: string;
  /** ISO end of the window (now). */
  end: string;
  /** False when the window is the 48h fallback (no prior login recorded). */
  hasPriorLogin: boolean;
}

export interface StationMetrics {
  /** Reports whose reported date falls inside the window. */
  reportsCreated: number;
  /** Reports touched in-window that are signed/finalized. */
  reportsSigned: number;
  /** Reports touched in-window that reached RMS COMMITTED. */
  reportsCommitted: number;
  /** Reports touched in-window that are awaiting review. */
  reportsInReview: number;
  /** Evidence files uploaded inside the window. */
  evidenceUploaded: number;
  /** Distinct officers who created/modified a report or uploaded evidence. */
  activeOfficers: number;
  /** Total reports with any activity in the window (the working set size). */
  reportsTouched: number;
  /** True when the report/evidence fetch hit its cap (metrics are a floor). */
  truncated: boolean;
}

export type ActivityKind = "report" | "evidence";

export interface ActivityEvent {
  id: string;
  kind: ActivityKind;
  /** Human title: report name/description, or evidence file name. */
  title: string;
  /** Report status (report events only). */
  status?: string;
  /** Resolved display name of the acting officer. */
  officerName: string;
  /** Officer's user id, when known (for de-dup / linking). */
  officerId?: string;
  /** ISO timestamp of the event. */
  timestamp: string;
}

// ---------------------------------------------------------------------------
// Reports
// ---------------------------------------------------------------------------

export interface StationReportsResult {
  reports: ReportGenReport[];
  /** True when either bounded server query had more matches than it returned. */
  truncated: boolean;
}

/**
 * Lists reports whose current metadata says they were either modified or
 * created between windowStart and windowEnd. RMS upper bounds are inclusive;
 * downstream aggregators apply the dashboard's exclusive end. The RMS API
 * combines filters with AND, so this requires two independently bounded
 * queries (updatedAt and createdAt) followed by an OR-style merge here. A row
 * returned by both queries keeps the modified-window response: if the two
 * reads straddle a later edit, that is the only response that can still carry
 * the pre-end last-touch timestamp.
 *
 * The RMS endpoint returns current report metadata, not immutable activity
 * history. Consequently, if a report is edited after windowEnd before either
 * read observes it, an earlier pre-end touch cannot be reconstructed here.
 * The createdAt query still preserves honest creation attribution, and both
 * queries are upper-bounded so post-end traffic cannot consume their caps.
 */
export async function listStationReportsSince(
  windowStart: string,
  windowEnd: string,
  signal?: AbortSignal,
): Promise<StationReportsResult> {
  const fetchPage = async (
    params: Record<string, string | number>,
  ): Promise<{ reports: ReportGenReport[]; truncated: boolean }> => {
    const response = await rmsClient.get("/reports", { params, signal });
    const reports: ReportGenReport[] = response.data?.reports ?? [];
    const rawTotal = response.data?.total;
    const total =
      typeof rawTotal === "number" && Number.isFinite(rawTotal)
        ? rawTotal
        : reports.length;
    return { reports, truncated: total > reports.length };
  };

  const common = {
    limit: REPORTS_FETCH_LIMIT,
    page: 0,
    sortOrder: "desc",
  } as const;
  const [modified, created] = await Promise.all([
    fetchPage({
      ...common,
      dateFrom: windowStart,
      dateTo: windowEnd,
      sortBy: "dateModified",
    }),
    fetchPage({
      ...common,
      reportedDateFrom: windowStart,
      reportedDateTo: windowEnd,
      sortBy: "reportedByDate",
    }),
  ]);

  const reportsById = new Map<string, ReportGenReport>();
  for (const report of modified.reports) reportsById.set(report.id, report);
  for (const report of created.reports) {
    if (!reportsById.has(report.id)) reportsById.set(report.id, report);
  }

  return {
    reports: [...reportsById.values()],
    truncated: modified.truncated || created.truncated,
  };
}

// ---------------------------------------------------------------------------
// Evidence
// ---------------------------------------------------------------------------

export interface StationEvidenceResult {
  items: EvidenceFileDDB[];
  /** True when more pages remained after the cap (count is a floor). */
  truncated: boolean;
}

/**
 * Pages through ingested evidence uploaded in [windowStart, windowEnd), up to
 * EVIDENCE_PAGE_CAP pages. Evidence upload timestamps are immutable, so the
 * upper server filter cannot hide pre-end activity the way report dateTo can.
 * Reuses the locker's own page fetcher so the query params/response contract
 * stay identical to the evidence list UI.
 */
export async function listStationEvidenceSince(
  windowStart: string,
  windowEnd: string,
  signal?: AbortSignal,
): Promise<StationEvidenceResult> {
  const filters: EvidenceListFilters = {
    createdFrom: windowStart,
    createdTo: windowEnd,
    sortBy: "createdAt",
    sortOrder: "desc",
    videoState: "ingested",
    limit: EVIDENCE_PAGE_SIZE,
  };

  const items: EvidenceFileDDB[] = [];
  let nextToken: string | undefined = undefined;
  let pages = 0;
  do {
    const page = await fetchEvidencePage(filters, nextToken, signal);
    items.push(...(page.evidence ?? []));
    nextToken = page.nextToken || undefined;
    pages += 1;
  } while (nextToken && pages < EVIDENCE_PAGE_CAP);

  return { items, truncated: Boolean(nextToken) };
}

// ---------------------------------------------------------------------------
// Aggregation (pure)
// ---------------------------------------------------------------------------

/** Resolves an officer's display name from the roster, falling back to a report
 *  attribution name or the raw id. */
export type NameResolver = (userId: string | undefined) => string | undefined;

/**
 * Shared aggregation core over an explicit [fromMs, toMs) touch range. A
 * report belongs to the range when its last touch (dateModified, falling back
 * to reportedByDate — the same timestamp that places it in the feed) lands
 * inside; reportsCreated is gated on the CREATION date alone, so "created"
 * never counts a report that was created earlier and merely edited in-range,
 * and a report created in-range but edited later still counts on the day it
 * was created. Evidence belongs by its createdAt (epoch ms).
 */
function aggregateMetricsForRange(
  reports: ReportGenReport[],
  evidenceItems: EvidenceFileDDB[],
  fromMs: number,
  toMs: number,
  truncated: boolean,
): StationMetrics {
  const inRange = (value: string | number | Date | undefined): boolean => {
    if (value === undefined || value === "") return false;
    const t = new Date(value).getTime();
    return !Number.isNaN(t) && t >= fromMs && t < toMs;
  };

  const officers = new Set<string>();
  let reportsCreated = 0;
  let reportsSigned = 0;
  let reportsCommitted = 0;
  let reportsInReview = 0;
  let reportsTouched = 0;

  for (const r of reports) {
    // Created must be counted BEFORE the touch gate: a report created on day A
    // and edited on day B has its touch on day B, and would otherwise appear
    // on no day chip's "created" count at all.
    if (inRange(String(r.reportedByDate || ""))) {
      reportsCreated += 1;
      if (r.reportedBy) officers.add(r.reportedBy);
    }
    if (!inRange(reportTouchValue(r))) continue;
    reportsTouched += 1;
    if (SIGNED_STATUSES.has(r.status)) reportsSigned += 1;
    if (r.status === ReportStatus.RMS_COMMITTED) reportsCommitted += 1;
    if (IN_REVIEW_STATUSES.has(r.status)) reportsInReview += 1;
    if (r.modifiedBy) officers.add(r.modifiedBy);
  }

  let evidenceUploaded = 0;
  for (const e of evidenceItems) {
    if (!inRange(e.createdAt)) continue;
    evidenceUploaded += 1;
    if (e.uploadedByUserId) officers.add(e.uploadedByUserId);
  }

  return {
    reportsCreated,
    reportsSigned,
    reportsCommitted,
    reportsInReview,
    evidenceUploaded,
    activeOfficers: officers.size,
    reportsTouched,
    truncated,
  };
}

/**
 * Whole-window metrics from [window.start, window.end). Inputs are wider at
 * the start (the hook fetches from local midnight) and the RMS upper filters
 * are inclusive; this range check restores the dashboard's precise bounds.
 */
export function aggregateMetrics(
  reports: ReportGenReport[],
  reportsTruncated: boolean,
  evidence: StationEvidenceResult,
  window: StationActivityWindow,
): StationMetrics {
  const truncated = reportsTruncated || evidence.truncated;
  return aggregateMetricsForRange(
    reports,
    evidence.items,
    new Date(window.start).getTime(),
    new Date(window.end).getTime(),
    truncated,
  );
}

// ---------------------------------------------------------------------------
// Day-scoped views (Station Overview / Pulse "group by day")
// ---------------------------------------------------------------------------

/** The activity timestamp that places a report in the feed and in day buckets
 *  (last touch, falling back to creation) — keep in sync with buildActivityFeed. */
function reportTouchValue(r: ReportGenReport): string {
  return String(r.dateModified || r.reportedByDate || "");
}

/** Local day-start for a report/evidence timestamp; null when unparseable. */
function localDayStart(value: string | number | Date | undefined): Date | null {
  if (value === undefined || value === "") return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export interface ActivityDayOption {
  /** Stable key (local y-m-d). */
  key: string;
  /** Local midnight starting the day. */
  start: Date;
}

export interface ActivityDayBounds {
  /** Inclusive lower bound, normally the window start floored to local midnight. */
  start: string;
  /** Exclusive frozen dashboard-snapshot upper bound. */
  end: string;
}

export function activityDayKey(dayStart: Date): string {
  return `${dayStart.getFullYear()}-${dayStart.getMonth()}-${dayStart.getDate()}`;
}

/** Distinct local days with any activity, newest first. Report creation and
 *  last-touch are independent activity dates; evidence uses createdAt. Bounds
 *  keep raw rows from manufacturing incomplete days outside the widened fetch
 *  range. */
export function listActivityDays(
  reports: ReportGenReport[],
  evidence: EvidenceFileDDB[],
  bounds?: ActivityDayBounds,
): ActivityDayOption[] {
  const byKey = new Map<string, Date>();
  const parsedStart = bounds ? new Date(bounds.start).getTime() : NaN;
  const parsedEnd = bounds ? new Date(bounds.end).getTime() : NaN;
  const fromMs = Number.isNaN(parsedStart)
    ? Number.NEGATIVE_INFINITY
    : parsedStart;
  const toMs = Number.isNaN(parsedEnd) ? Number.POSITIVE_INFINITY : parsedEnd;
  const add = (value: string | number | Date | undefined) => {
    if (value === undefined || value === "") return;
    const timestamp = new Date(value);
    if (
      Number.isNaN(timestamp.getTime()) ||
      timestamp.getTime() < fromMs ||
      timestamp.getTime() >= toMs
    ) {
      return;
    }
    const start = new Date(
      timestamp.getFullYear(),
      timestamp.getMonth(),
      timestamp.getDate(),
    );
    const key = activityDayKey(start);
    if (!byKey.has(key)) byKey.set(key, start);
  };
  for (const r of reports) {
    add(r.reportedByDate);
    add(reportTouchValue(r));
  }
  for (const e of evidence) add(e.createdAt);
  return [...byKey.entries()]
    .map(([key, start]) => ({ key, start }))
    .sort((a, b) => b.start.getTime() - a.start.getTime());
}

/**
 * StationMetrics for a single local day: [dayStart, nextDay) over the shared
 * range core (see aggregateMetricsForRange for the touch/created semantics).
 * Next-midnight comes from the date constructor (DST-safe; never a fixed 24h).
 * The optional frozen window end preserves the dashboard's exclusive upper
 * bound even though the RMS server filters are inclusive; earlier complete
 * days are unaffected.
 *
 * `truncated` is threaded from the whole-window fetch: when that was capped,
 * day figures are floors for the same reason the window figures are.
 */
export function aggregateMetricsForDay(
  reports: ReportGenReport[],
  evidenceItems: EvidenceFileDDB[],
  dayStart: Date,
  truncated: boolean,
  windowEnd?: string,
): StationMetrics {
  const nextDay = new Date(
    dayStart.getFullYear(),
    dayStart.getMonth(),
    dayStart.getDate() + 1,
  ).getTime();
  const parsedWindowEnd = windowEnd ? new Date(windowEnd).getTime() : NaN;
  const toMs = Number.isNaN(parsedWindowEnd)
    ? nextDay
    : Math.min(nextDay, parsedWindowEnd);
  return aggregateMetricsForRange(
    reports,
    evidenceItems,
    dayStart.getTime(),
    toMs,
    truncated,
  );
}

/**
 * The fetch start that makes day groupings honest: the local-midnight floor
 * of the window start, so the first day is not clipped at the login moment.
 * Whole-window figures stay precise because aggregateMetrics /
 * filterActivitySince re-scope to the exact window. Unparseable input falls
 * through unchanged.
 */
export function widenToLocalDayStart(iso: string): string {
  const start = localDayStart(iso);
  return start ? start.toISOString() : iso;
}

/**
 * Window-precise subset of a widened fetch: activity whose feed timestamp
 * (report last-touch / evidence createdAt) falls in [fromIso, toIso). Used to
 * keep the activity feed and the AI briefing scoped to the frozen
 * since-last-login snapshot while the day chips see complete days.
 */
export function filterActivitySince(
  reports: ReportGenReport[],
  evidenceItems: EvidenceFileDDB[],
  fromIso: string,
  toIso?: string,
): { reports: ReportGenReport[]; evidence: EvidenceFileDDB[] } {
  const from = new Date(fromIso).getTime();
  const to = toIso ? new Date(toIso).getTime() : Number.POSITIVE_INFINITY;
  if (Number.isNaN(from) || Number.isNaN(to)) {
    return { reports, evidence: evidenceItems };
  }
  return {
    reports: reports.filter((r) => {
      const t = new Date(reportTouchValue(r)).getTime();
      return !Number.isNaN(t) && t >= from && t < to;
    }),
    evidence: evidenceItems.filter((e) => {
      const t = new Date(e.createdAt).getTime();
      return !Number.isNaN(t) && t >= from && t < to;
    }),
  };
}

/**
 * Builds a single reverse-chronological activity feed from reports + evidence.
 * Report events use the server-provided attribution names; evidence events
 * resolve the uploader via the roster name resolver.
 *
 * Deliberately uncapped: the inputs are already bounded by the fetch layer
 * (REPORTS_FETCH_LIMIT / EVIDENCE_PAGE_CAP, surfaced via metrics.truncated),
 * the UI paginates client-side (ActivityFeed), and the AI briefing trims its
 * own slice (MAX_EVENTS_FOR_SUMMARY). A cap here would silently drop events
 * while the pagination footer claims to show everything.
 */
export function buildActivityFeed(
  reports: ReportGenReport[],
  evidence: EvidenceFileDDB[],
  resolveName: NameResolver,
): ActivityEvent[] {
  const events: ActivityEvent[] = [];

  for (const r of reports) {
    const timestampValue = r.dateModified || r.reportedByDate;
    if (!timestampValue) continue;
    const timestamp = new Date(timestampValue);
    if (!Number.isFinite(timestamp.getTime())) continue;

    const officerName =
      r.modifiedByName ||
      r.reportedByName ||
      resolveName(r.modifiedBy) ||
      resolveName(r.reportedBy) ||
      "Unknown officer";
    events.push({
      id: `report:${r.id}`,
      kind: "report",
      title: r.reportName || r.details || `Report ${r.id}`,
      status: r.status,
      officerName,
      officerId: r.modifiedBy || r.reportedBy,
      // dateModified is the wire ISO string; reportedByDate is the fallback.
      timestamp: timestamp.toISOString(),
    });
  }

  for (const e of evidence) {
    // Evidence dates cross the network boundary despite the generated numeric
    // type. Reject malformed wire values before Date#toISOString can throw.
    if (typeof e.createdAt !== "number" || !Number.isFinite(e.createdAt)) {
      continue;
    }
    const createdAt = new Date(e.createdAt);
    if (Number.isNaN(createdAt.getTime())) continue;

    events.push({
      id: `evidence:${e.evidenceId}`,
      kind: "evidence",
      title: e.fileName || "Evidence upload",
      officerName: resolveName(e.uploadedByUserId) || "Unknown officer",
      officerId: e.uploadedByUserId,
      timestamp: createdAt.toISOString(),
    });
  }

  return events
    .filter((e) => e.timestamp)
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
}

/** Cap on per-author entries sent to the briefing model. Mirrors the server's
 *  own bound; the top few authors are all a 1-2 sentence briefing can use. */
export const MAX_OFFICER_ACTIVITY = 5;

export interface OfficerActivity {
  /** Resolved display name of the report author. */
  name: string;
  /** Reports this officer created inside the window. */
  reportsCreated: number;
}

/**
 * Top report authors for the AI briefing: counts reports CREATED in
 * [fromIso, toIso) — the same reportedByDate semantics as
 * StationMetrics.reportsCreated — attributed to the reporting officer.
 * Reports whose author resolves to no name are left out (the aggregate counts
 * still include them): the model must never receive fabricated identities,
 * and an "Unknown officer" bucket would invite exactly that.
 */
export function aggregateOfficerActivity(
  reports: ReportGenReport[],
  fromIso: string,
  resolveName: NameResolver,
  limit: number = MAX_OFFICER_ACTIVITY,
  toIso?: string,
): OfficerActivity[] {
  const from = new Date(fromIso).getTime();
  const to = toIso ? new Date(toIso).getTime() : Number.POSITIVE_INFINITY;
  if (Number.isNaN(from) || Number.isNaN(to)) return [];
  const byAuthor = new Map<string, OfficerActivity>();
  for (const r of reports) {
    const created = new Date(String(r.reportedByDate || "")).getTime();
    if (Number.isNaN(created) || created < from || created >= to) continue;
    const name = r.reportedByName || resolveName(r.reportedBy) || "";
    if (!name) continue;
    // Key on the author id when known so name-snapshot drift across reports
    // still counts as one officer; id-less rows key on the name itself.
    const key = r.reportedBy || `name:${name}`;
    const entry = byAuthor.get(key);
    if (entry) entry.reportsCreated += 1;
    else byAuthor.set(key, { name, reportsCreated: 1 });
  }
  // Collapse identical display names (an id-keyed and an id-less row, or two
  // badge-less officers who render the same): the briefing speaks at name
  // granularity, and duplicate "Alice" lines read as a glitch.
  const byName = new Map<string, OfficerActivity>();
  for (const entry of byAuthor.values()) {
    const existing = byName.get(entry.name);
    if (existing) existing.reportsCreated += entry.reportsCreated;
    else byName.set(entry.name, { ...entry });
  }
  return [...byName.values()]
    .sort(
      (a, b) =>
        b.reportsCreated - a.reportsCreated || a.name.localeCompare(b.name),
    )
    .slice(0, limit);
}

// ---------------------------------------------------------------------------
// AI summary
// ---------------------------------------------------------------------------

export interface SummarizeActivityRequest {
  window: StationActivityWindow;
  stationName?: string;
  metrics: StationMetrics;
  /** A trimmed slice of the feed for grounding (kept small + low-PII). */
  events: Array<Pick<ActivityEvent, "kind" | "title" | "status" | "officerName" | "timestamp">>;
  /** Top in-window report authors (bounded, resolved names only) so the
   *  briefing can say who drove the report activity. */
  officerActivity?: OfficerActivity[];
}

export interface SummarizeActivityResponse {
  summary: string;
}

export const SUMMARY_POLL_INTERVAL_MS = 2000;
// Leave a full minute beyond the worker's five-minute hard limit for its
// final DynamoDB write and the next status poll to reach the browser.
export const SUMMARY_POLL_TIMEOUT_MS = 6 * 60_000;

type SummarizeActivityJobResponse = {
  jobId: string;
  status: "pending" | "running" | "completed" | "failed";
  summary?: string;
  error?: string;
};

function abortableDelay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const abortError = () =>
      signal?.reason ?? new DOMException("Aborted", "AbortError");

    if (signal?.aborted) {
      reject(abortError());
      return;
    }

    const onAbort = () => {
      clearTimeout(timer);
      reject(abortError());
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

function completedSummary(
  result: SummarizeActivityJobResponse,
): SummarizeActivityResponse {
  const summary = result.summary?.trim();
  if (!summary) {
    throw new Error("Activity summary completed without a summary");
  }
  return { summary };
}

/**
 * Asks the AI service to turn the aggregated station activity into a short
 * natural-language briefing for the admin. Served by the MCP agent Lambda via
 * aiClient's configured agent API base URL. Starts an asynchronous job and
 * polls until the finished text is available.
 */
export async function summarizeStationActivity(
  req: SummarizeActivityRequest,
  signal?: AbortSignal,
): Promise<SummarizeActivityResponse> {
  const deadline = Date.now() + SUMMARY_POLL_TIMEOUT_MS;
  const response = await aiClient.post<SummarizeActivityJobResponse>(
    "/summarize-station-activity",
    req,
    { signal },
  );
  const started = response.data;
  const jobId = started.jobId?.trim();
  if (!jobId) {
    throw new Error("Activity summary job did not return a job ID");
  }
  if (started.status === "completed") return completedSummary(started);
  if (started.status === "failed") {
    throw new Error(started.error?.trim() || "Activity summary generation failed");
  }
  if (
    started.status !== "pending" &&
    started.status !== "running"
  ) {
    throw new Error(`Unexpected activity summary status: ${started.status}`);
  }

  while (Date.now() < deadline) {
    const remainingMs = deadline - Date.now();
    await abortableDelay(
      Math.min(SUMMARY_POLL_INTERVAL_MS, remainingMs),
      signal,
    );
    if (Date.now() >= deadline) break;

    let pollResponse;
    try {
      pollResponse = await aiClient.get<SummarizeActivityJobResponse>(
        "/summarize-station-activity",
        { params: { jobId }, signal },
      );
    } catch (error) {
      if (signal?.aborted || !axios.isAxiosError(error) || error.response) {
        throw error;
      }
      // A brief connection failure must not start a second billable job via
      // the UI's Retry action. Keep polling this job; HTTP errors still fail.
      continue;
    }
    const result = pollResponse.data;
    if (result.status === "completed") return completedSummary(result);
    if (result.status === "failed") {
      throw new Error(
        result.error?.trim() || "Activity summary generation failed",
      );
    }
    if (result.status !== "pending" && result.status !== "running") {
      throw new Error(`Unexpected activity summary status: ${result.status}`);
    }
  }

  throw new Error("Activity summary generation timed out");
}
